import { ComponentProps, forwardRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';
import {
  errorTextClasses,
  helperTextClasses,
  labelClasses,
  textAreaCharactersCountClasses,
  textareaClasses,
  textareaGroupClasses,
} from './variants';

type TextareaProps = ComponentProps<'textarea'> &
  VariantProps<typeof textareaGroupClasses> & {
    label?: string;
    helperText?: string;
    error?: boolean;
    errorMessage?: string | any;
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  };

const TextArea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      variant = 'md',
      label,
      helperText,
      error,
      errorMessage,
      disabled,
      resize,
      ...props
    },
    ref
  ) => {
    const value = props.value ? props.value + '' : '';
    const hasErrorMessage = !!errorMessage;
    const hasError = error || hasErrorMessage;

    const hasErrorOrHelperText = hasErrorMessage || !!helperText;

    const [charactersCount, setCharactersCount] = useState(value.length);

    return (
      <div className={textareaGroupClasses({ variant })}>
        <textarea
          {...props}
          placeholder={props.placeholder || ' '}
          ref={ref}
          disabled={disabled}
          onChange={(e) => {
            props.onChange && props.onChange(e);
            setCharactersCount(e.target.value.length);
          }}
          className={textareaClasses({
            variant,
            hasError,
            disabled,
            resize,
          })}
        />

        {label && (
          <label
            htmlFor={props.id}
            className={labelClasses({ variant, disabled, forceUp: !!props.placeholder })}
          >
            {label}
          </label>
        )}

        <span
          className={textAreaCharactersCountClasses({
            disabled,
            hasErrorOrHelperText,
          })}
        >
          {charactersCount || 0}
          {props.maxLength ? `/${props.maxLength}` : ''}
        </span>

        {helperText && !hasErrorMessage && (
          <span className={helperTextClasses({ disabled })}>{helperText}</span>
        )}

        {hasErrorMessage && (
          <span className={errorTextClasses({ variant, disabled })}>
            {errorMessage}
          </span>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export { TextArea };
